<script setup lang="ts">
import { ArrowRightIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import LzpCommandChooser from './LzpCommandChooser.vue';
import LzpSchedulePicker from './LzpSchedulePicker.vue';
import { useIssueCommandForm } from '@/composables/useIssueCommandForm';

/**
 * Sticky bulk-action bar shown when one or more Location Zone Player cards are
 * checked. Reads as one sentence — "Issue <command> to N players, <when>" — so
 * the player count appears once; pick an issuable catalogue command, optionally
 * schedule it, and issue to every selected player at once. All issue logic (command
 * selection, raw mode, client gating, scheduling, the mutation + result toast) is
 * shared with the single-player inspector control via {@link useIssueCommandForm};
 * this component is just the bulk presentation.
 */
const props = defineProps<{ players: Record<string, unknown>[] }>();
const emit = defineEmits<{ (e: 'clear'): void }>();

const {
    enumsQuery,
    issueMutation,
    selectedCommand,
    canIssueRawCommand,
    rawMode,
    rawCommand,
    scheduleDate,
    scheduleTime,
    minScheduleDate,
    usePlayerTime,
    browserTimezone,
    browserTimezoneAbbreviation,
    eligibility,
    selectCommand,
    optionGateReason,
    issuePayloadCommand,
    commandLabel,
    scheduledAt,
    scheduledInPast,
    scheduleLabel,
    issue,
} = useIssueCommandForm(() => props.players);
</script>

<template>
    <div
        class="bg-background/95 supports-[backdrop-filter]:bg-background/80 border-border/80 sticky top-0 z-10 mb-2 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 backdrop-blur"
        data-testid="lzp-bulk-issue-bar"
    >
        <button
            type="button"
            class="text-muted-foreground hover:text-foreground hover:bg-muted/40 border-border/60 inline-flex size-6 shrink-0 items-center justify-center rounded-md border"
            data-testid="lzp-bulk-clear"
            aria-label="Clear selection"
            @click="emit('clear')"
        >
            <XMarkIcon class="size-3.5" aria-hidden="true" />
        </button>

        <!-- The action reads as one sentence so the player count appears exactly once. -->
        <div class="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span>Issue</span>

            <LzpCommandChooser
                v-model:raw-mode="rawMode"
                v-model:raw-command="rawCommand"
                :commands="enumsQuery.data.value ?? []"
                :loading="enumsQuery.isLoading.value"
                :selected="selectedCommand"
                :can-issue-raw="canIssueRawCommand"
                :option-gate-reason="optionGateReason"
                test-id-prefix="lzp-bulk"
                @select="selectCommand"
                @enter="issue()"
            />

            <span>
                to <span class="text-foreground font-medium">{{ players.length }} player{{ players.length === 1 ? '' : 's' }}</span><span
                    v-if="selectedCommand && eligibility.ineligible.length > 0"
                    data-testid="lzp-bulk-eligibility"
                >
                    · {{ eligibility.eligible.length }} eligible</span>,
            </span>

            <!-- Schedule chip: "now" = issue immediately; a pick opens the calendar + time + player-time toggle. -->
            <LzpSchedulePicker
                v-model:date="scheduleDate"
                v-model:time="scheduleTime"
                v-model:use-player-time="usePlayerTime"
                :min-value="minScheduleDate"
                :scheduled-at="scheduledAt"
                :scheduled-in-past="scheduledInPast"
                :schedule-label="scheduleLabel"
                :browser-timezone="browserTimezone"
                :browser-timezone-abbreviation="browserTimezoneAbbreviation"
                test-id-prefix="lzp-bulk-schedule"
            />
        </div>

        <!-- Compact send button: the count lives in the sentence, so this is the bare action. -->
        <button
            type="button"
            class="bg-primary text-primary-foreground hover:bg-primary/90 ml-auto inline-flex size-7 shrink-0 items-center justify-center rounded-md disabled:opacity-50"
            :disabled="!issuePayloadCommand || issueMutation.isPending.value || scheduledInPast"
            data-testid="lzp-bulk-issue"
            :aria-label="`Issue ${commandLabel || 'command'} to ${players.length} player${players.length === 1 ? '' : 's'}`"
            :title="issueMutation.isPending.value ? 'Issuing…' : 'Issue'"
            @click="issue()"
        >
            <ArrowRightIcon class="size-4" aria-hidden="true" />
        </button>
    </div>
</template>
